import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Trash2, Search, Users, Rss } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REC_LABEL = {
  all: "Todos os sócios", direcao: "Direção / órgãos sociais",
  community: "Comunidade", custom: "Sócios escolhidos",
};

const EMPTY = {
  subject: "", body: "", link_url: "", link_label: "",
  recipients_type: "all", community_id: "", member_ids: [],
};

export default function AdminComunicados() {
  const [f, setF] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }));

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error("Erro a carregar o histórico."); }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/communities`).then((r) => setCommunities(r.data.items || r.data || [])).catch(() => {});
    axios.get(`${API}/admin/members`, { withCredentials: true })
      .then((r) => setMembers((r.data.items || []).filter((m) => m.status === "approved")))
      .catch(() => {});
  }, []);

  // Contagem de destinatários em tempo real
  useEffect(() => {
    const t = setTimeout(async () => {
      if (f.recipients_type === "community" && !f.community_id) { setPreview(null); return; }
      if (f.recipients_type === "custom" && !f.member_ids.length) { setPreview({ count: 0, sample: [] }); return; }
      try {
        const r = await axios.post(`${API}/admin/announcements/preview-recipients`,
          { ...f, subject: "x", body: "x" }, { withCredentials: true });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [f.recipients_type, f.community_id, f.member_ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q));
  }, [members, search]);

  const toggleMember = (id) =>
    set("member_ids", f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id]);

  const sendNow = async () => {
    if (!f.subject.trim() || !f.body.trim()) { toast.error("Preencha o assunto e a mensagem."); return; }
    const n = preview?.count ?? "?";
    if (!window.confirm(`Enviar este comunicado a ${n} destinatário(s) agora?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/announcements`, {
        ...f,
        community_id: f.recipients_type === "community" ? f.community_id : null,
        member_ids: f.recipients_type === "custom" ? f.member_ids : null,
      }, { withCredentials: true });
      toast.success(`Comunicado enviado a ${r.data.emails_sent} destinatário(s).`);
      setF(EMPTY); setSearch("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar.");
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Apagar do histórico "${a.subject}"?`)) return;
    try {
      await axios.delete(`${API}/admin/announcements/${a.id}`, { withCredentials: true });
      load();
    } catch { toast.error("Erro a apagar."); }
  };

  const inputCls = "w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm";
  const lbl = "block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1";

  return (
    <div className="p-6 lg:p-10" data-testid="admin-comunicados">
      <div className="overline">Avisos &amp; Notícias</div>
      <h1 className="display-heading text-3xl mt-1">Comunicados.</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
        Envio imediato por email, personalizado com o nome de cada sócio. Para reuniões com
        confirmação de presença use as <a href="/admin/convocatorias" className="underline text-[hsl(var(--primary))]">Convocatórias</a>.
      </p>

      <div className="mt-8 bg-white border border-[hsl(var(--border))] p-6 space-y-4 max-w-3xl" data-testid="comunicado-form">
        <div>
          <label className={lbl}>Assunto *</label>
          <input value={f.subject} onChange={(e) => set("subject", e.target.value)}
            placeholder='Ex.: "Aviso — alteração do local da reunião de sábado"' className={inputCls} data-testid="com-subject" />
        </div>
        <div>
          <label className={lbl}>Mensagem *</label>
          <textarea value={f.body} onChange={(e) => set("body", e.target.value)} rows={7}
            placeholder="Escreva aqui o comunicado…" className={inputCls} data-testid="com-body" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Link (opcional)</label>
            <input value={f.link_url} onChange={(e) => set("link_url", e.target.value)}
              placeholder="https://…" className={inputCls} data-testid="com-link" />
          </div>
          <div>
            <label className={lbl}>Texto do botão do link</label>
            <input value={f.link_label} onChange={(e) => set("link_label", e.target.value)}
              placeholder='"Saber mais" (por omissão)' className={inputCls} data-testid="com-link-label" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Destinatários</label>
            <select value={f.recipients_type} onChange={(e) => set("recipients_type", e.target.value)} className={inputCls} data-testid="com-recipients">
              <option value="all">Todos os sócios aprovados</option>
              <option value="direcao">Só direção / órgãos sociais</option>
              <option value="community">Só uma comunidade</option>
              <option value="custom">Escolher sócios específicos</option>
            </select>
          </div>
          {f.recipients_type === "community" && (
            <div>
              <label className={lbl}>Comunidade</label>
              <select value={f.community_id || ""} onChange={(e) => set("community_id", e.target.value)} className={inputCls} data-testid="com-community">
                <option value="">— Escolher —</option>
                {communities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {f.recipients_type === "custom" && (
          <div className="border border-[hsl(var(--border))] p-3" data-testid="com-member-picker">
            <div className="flex items-center gap-2 mb-2">
              <Search className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar sócio por nome ou email…"
                className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid="com-member-search" />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{f.member_ids.length} escolhido(s)</span>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-0.5">
              {filteredMembers.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm px-1 py-0.5 hover:bg-[hsl(var(--secondary))] cursor-pointer">
                  <input type="checkbox" checked={f.member_ids.includes(m.id)} onChange={() => toggleMember(m.id)}
                    data-testid={`com-member-${m.id}`} />
                  {m.name} <span className="text-xs text-[hsl(var(--muted-foreground))]">({m.email})</span>
                </label>
              ))}
              {!filteredMembers.length && <p className="text-xs text-[hsl(var(--muted-foreground))] p-1">Sem resultados.</p>}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={sendNow} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="com-send">
            <Send className="h-3.5 w-3.5" /> {busy ? "A enviar…" : "Enviar agora"}
          </button>
          {preview && (
            <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]" data-testid="com-preview-count">
              <Users className="h-3.5 w-3.5" /> {preview.count} destinatário(s)
              {preview.sample?.length ? ` — ${preview.sample.slice(0, 3).join(", ")}${preview.count > 3 ? "…" : ""}` : ""}
            </span>
          )}
        </div>
      </div>

      <div className="mt-10 max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-3">Histórico de envios</div>
        {!items.length && <p className="text-sm text-[hsl(var(--muted-foreground))]">Ainda sem comunicados enviados.</p>}
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3" data-testid={`comunicado-${a.id}`}>
              <div>
                <div className="font-medium text-sm flex items-center gap-2">
                  <Rss className="h-4 w-4 text-[hsl(var(--accent))]" /> {a.subject}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {new Date(a.sent_at).toLocaleString("pt-PT")} · {REC_LABEL[a.recipients_type]}
                  {a.community_id ? ` (${a.community_id})` : ""} · {a.emails_sent}/{a.recipient_count} emails enviados
                </div>
              </div>
              <button onClick={() => remove(a)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700 shrink-0" data-testid={`com-delete-${a.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
